import { FormControlLabel, IconButton, Switch } from '@mui/material';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import { hf, hfAlpha, useHfThemeMode } from '../../theme';
import { HFTooltip } from '../HFTooltip';

export type HFThemeToggleProps = {
  /** `icon` for compact chrome (login); `switch` for settings. */
  variant?: 'icon' | 'switch';
};

export function HFThemeToggle({ variant = 'icon' }: HFThemeToggleProps) {
  const { mode, toggleMode, setMode } = useHfThemeMode();
  const isDark = mode === 'dark';

  if (variant === 'switch') {
    return (
      <FormControlLabel
        sx={{
          m: 0,
          width: '100%',
          justifyContent: 'space-between',
          gap: 2,
          '& .MuiFormControlLabel-label': { flex: 1 },
        }}
        labelPlacement="start"
        control={
          <Switch
            checked={isDark}
            onChange={(_, checked) => setMode(checked ? 'dark' : 'light')}
            color="primary"
            slotProps={{ input: { 'aria-label': 'Dark mode' } }}
            sx={{
              width: 68,
              height: 40,
              padding: 0.75,
              '& .MuiSwitch-switchBase': {
                padding: 1,
                '&.Mui-checked': {
                  transform: 'translateX(28px)',
                },
              },
              '& .MuiSwitch-thumb': {
                width: 24,
                height: 24,
              },
              '& .MuiSwitch-track': {
                borderRadius: 20,
                opacity: 1,
              },
            }}
          />
        }
        label={<span className="text-base font-semibold">Dark mode</span>}
      />
    );
  }

  return (
    <HFTooltip title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}>
      <IconButton
        onClick={toggleMode}
        color="inherit"
        size="small"
        aria-label="Toggle theme"
        sx={{
          bgcolor: isDark ? hfAlpha(hf.white, 0.12) : hfAlpha(hf.navy700, 0.06),
          color: isDark ? hf.white : hf.navy700,
          border: isDark ? `1px solid ${hfAlpha(hf.white, 0.25)}` : `1px solid ${hfAlpha(hf.navy700, 0.12)}`,
          '&:hover': {
            bgcolor: isDark ? hfAlpha(hf.white, 0.2) : hfAlpha(hf.navy700, 0.1),
          },
        }}
      >
        {isDark ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
      </IconButton>
    </HFTooltip>
  );
}
